import { toUTCFormat } from "@/lib/dateHelpers";
import { Path } from "@/router";
import type { AdditionalAlgoliaIndex } from "./useAlgoliaSearch";
import type { ActivityHit, GlobalHit } from "./types";

/** Mixed Quick Search lists cap activity rows at this count. Activity-only lists fill leftover dropdown slots. */
export const GLOBAL_QUICK_SEARCH_ACTIVITY_SLOTS = 2;
export const GLOBAL_SEARCH_ALL_ACTIVITY_SLOTS = 3;

export function getActivitiesSearchIndexName(): string | undefined {
  const name = import.meta.env.VITE_ACTIVITIES_SEARCH_INDEX_NAME;
  return typeof name === "string" && name.trim() ? name.trim() : undefined;
}

export function toActivityHit(raw: Record<string, unknown>): ActivityHit {
  return {
    objectID: String(raw.objectID ?? ""),
    object_type: "Activity",
    title: typeof raw.title === "string" ? raw.title : "",
    internal_description: typeof raw.internal_description === "string" ? raw.internal_description : null,
    activity_type: typeof raw.activity_type === "string" ? raw.activity_type : null,
    activity_type_id: raw.activity_type_id == null ? null : String(raw.activity_type_id),
    managing_section_id: raw.managing_section_id == null ? null : String(raw.managing_section_id),
    managing_section_name: typeof raw.managing_section_name === "string" ? raw.managing_section_name : null,
    allow_participation_starting:
      typeof raw.allow_participation_starting === "string" ? raw.allow_participation_starting : null,
    allow_participation_through:
      typeof raw.allow_participation_through === "string" ? raw.allow_participation_through : null,
    has_event: Boolean(raw.has_event),
    event_title: typeof raw.event_title === "string" ? raw.event_title : null,
    event_start_date: typeof raw.event_start_date === "string" ? raw.event_start_date : null,
    event_end_date: typeof raw.event_end_date === "string" ? raw.event_end_date : null,
    _highlightResult: raw._highlightResult as Record<string, unknown> | undefined
  };
}

export function isNavigableActivityHit(hit: ActivityHit): boolean {
  return Boolean(hit.objectID && hit.activity_type_id);
}

export function mapActivityHits(hits: Array<Record<string, unknown>>): ActivityHit[] {
  return hits.map(toActivityHit).filter(isNavigableActivityHit);
}

export function getActivityHitPath(hit: ActivityHit): Path | null {
  if (!isNavigableActivityHit(hit) || !hit.activity_type_id) {
    return null;
  }

  return `/activities/activity-types/${hit.activity_type_id}/activity/${hit.objectID}` as Path;
}

export function getActivityDisplayName(hit: ActivityHit): string {
  return hit.title.trim() || "Untitled Activity";
}

function formatDateRange(startIso?: string | null, endIso?: string | null): string | undefined {
  const start = startIso ? toUTCFormat(startIso) : null;
  const end = endIso ? toUTCFormat(endIso) : null;

  if (start && end) {
    return `${start} – ${end}`;
  }

  return start || end || undefined;
}

export function getActivityDateRange(hit: ActivityHit): string | undefined {
  if (hit.allow_participation_starting || hit.allow_participation_through) {
    return formatDateRange(hit.allow_participation_starting, hit.allow_participation_through);
  }

  return formatDateRange(hit.event_start_date, hit.event_end_date);
}

export function getActivityListIcon(_hit?: ActivityHit): "assignment" {
  return "assignment";
}

export function getActivityListOverline(hit: ActivityHit): string | undefined {
  const typeTitle = typeof hit.activity_type === "string" ? hit.activity_type.trim() : "";
  return typeTitle ? `Type: ${typeTitle}` : undefined;
}

export function createActivityAdditionalSearch(maxHits: number): AdditionalAlgoliaIndex<GlobalHit> | undefined {
  const indexName = getActivitiesSearchIndexName();
  if (!indexName) {
    return undefined;
  }

  return {
    indexName,
    maxHits,
    mapHits: (hits) => mapActivityHits(hits)
  };
}
